/**
 * Design mode context — persisted palette / chrome variant for all pages.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  DESIGN_MODES,
  DEFAULT_DESIGN_MODE,
  PUBLIC_DESIGN_MODES,
  parseStoredDesignMode,
  type DesignMode,
  DESIGN_MODE_STORAGE_KEY_V2,
  DESIGN_MODE_STORAGE_KEY_V1,
} from '../config/theme';

export type { DesignMode };
export { DESIGN_MODES, PUBLIC_DESIGN_MODES, DEFAULT_DESIGN_MODE };

interface DesignModeContextValue {
  designMode: DesignMode;
  setDesignMode: (mode: DesignMode) => void;
}

const DesignModeContext = createContext<DesignModeContextValue | null>(null);

export function DesignModeProvider({ children }: { children: React.ReactNode }) {
  const [designMode, setDesignModeState] = useState<DesignMode>(DEFAULT_DESIGN_MODE);

  useEffect(() => {
    try {
      const v2 = localStorage.getItem(DESIGN_MODE_STORAGE_KEY_V2);
      const v1 = localStorage.getItem(DESIGN_MODE_STORAGE_KEY_V1);
      const mode = parseStoredDesignMode(v2, v1);
      setDesignModeState(mode);
      if (v2 !== String(mode)) localStorage.setItem(DESIGN_MODE_STORAGE_KEY_V2, String(mode));
    } catch {
      // ignore
    }
  }, []);

  const setDesignMode = (mode: DesignMode) => {
    setDesignModeState(mode);
    try {
      localStorage.setItem(DESIGN_MODE_STORAGE_KEY_V2, String(mode));
    } catch {
      // ignore
    }
  };

  return (
    <DesignModeContext.Provider value={{ designMode, setDesignMode }}>
      {children}
    </DesignModeContext.Provider>
  );
}

export function useDesignMode() {
  const ctx = useContext(DesignModeContext);
  if (!ctx) {
    return { designMode: DEFAULT_DESIGN_MODE as DesignMode, setDesignMode: () => {} };
  }
  return ctx;
}
